// Fix wave item 5: the /dashboard/loras gallery's "Use in Playground" CTA
// (loras/index.tsx's handleUseInPlayground) does a full navigate() to
// /dashboard/playground?style=<id> — remounting this page. `prompt` here is
// a bare useState(''), so a user who had already typed a prompt, then went
// to browse styles, landed back with it silently wiped.
//
// sessionStorage (not localStorage) is the deliberate fix: it's scoped to
// this browser tab's lifetime, so a draft survives an in-session
// remount/navigation but can never resurface in an unrelated later session
// (new tab, closed-and-reopened browser, a different day) — there is no
// separate expiry logic beyond that boundary.
//
// One case sessionStorage's own lifetime does NOT cover: logout. A tab can
// stay open across a sign-out on a shared machine, so without an explicit
// clear, user B signing in in the SAME tab right after user A logs out would
// inherit whatever A had typed — prompts are exactly the content that must
// never cross a user boundary. clearPromptDraft() below is that explicit
// clear; auth-store.ts's logout() calls it so this module stays the single
// owner of the storage key (callers never touch sessionStorage directly).

const PROMPT_DRAFT_KEY = 'playground.promptDraft';

export function readPromptDraft(): string {
  if (typeof window === 'undefined') return '';
  try {
    return window.sessionStorage.getItem(PROMPT_DRAFT_KEY) ?? '';
  } catch {
    return '';
  }
}

export function writePromptDraft(value: string): void {
  if (typeof window === 'undefined') return;
  try {
    if (value) {
      window.sessionStorage.setItem(PROMPT_DRAFT_KEY, value);
    } else {
      // Don't let an empty draft linger as a stored '' — keeps the storage
      // tidy and makes "no draft" and "empty draft" the same observable state.
      window.sessionStorage.removeItem(PROMPT_DRAFT_KEY);
    }
  } catch {
    // sessionStorage may be unavailable in private mode — non-fatal; the
    // draft simply doesn't survive a remount.
  }
}

/**
 * Explicitly discard any stored draft. Called from auth-store.ts's logout()
 * so a shared machine never lets the next sign-in in the same tab see the
 * previous user's unsent prompt text (privacy fix — see module comment).
 */
export function clearPromptDraft(): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.removeItem(PROMPT_DRAFT_KEY);
  } catch {
    // sessionStorage may be unavailable in private mode — non-fatal, same as
    // writePromptDraft(): there's nothing readable to clear in that case.
  }
}
